import React, { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import * as tus from "tus-js-client";

// Material UI
import Paper from "@mui/material/Paper";
import { makeStyles } from "@mui/styles";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import GroupIcon from "@mui/icons-material/Group";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

// My Files
import { UserContext, DatabaseContext } from "../../../context";
import firebase from "../../../firebase";
import LinkFileToEventDialog from "./LinkFileToEventDialog";
import FilePermissionsDialog from "./FilePermissionsDialog";
import FileNotificationsDialog from "./FileNotificationsDialog";
import { jsonFromArray, getAncestorHubs } from "../../../utility";
import { sendSharedItemEmails } from "../../shareDialogDelivery";
import uploadManager from "../../../uploadManager";
import { primaryHoverBorder } from "../../../surfaceTints";

const useStyles = makeStyles(() => ({
    pdfPreview: {
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        opacity: 0,
        pointerEvents: "none",
    },
}));

const settingButtonSx = {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.25,
    p: 1.35,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
    textAlign: "left",
    "&:hover": {
        borderColor: primaryHoverBorder,
        backgroundColor: "action.hover",
        boxShadow: "0 3px 12px rgba(0, 0, 0, 0.12)",
    },
};

function UploadFileTypeIcon({ type }) {
    if (type?.includes("video")) {
        return <MovieOutlinedIcon />;
    }

    if (type?.includes("audio")) {
        return <AudiotrackOutlinedIcon />;
    }

    if (type?.includes("image")) {
        return <ImageOutlinedIcon />;
    }

    if (type === "application/pdf") {
        return <PictureAsPdfOutlinedIcon />;
    }

    return <InsertDriveFileOutlinedIcon />;
}

function AvailabilityRow({
    icon,
    title,
    description,
    checked,
    onChange,
    name,
    divided = false,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: { xs: 1.5, sm: 2 },
                py: 1.35,
                borderTop: divided ? "1px solid" : "none",
                borderColor: "divider",
            }}
        >
            <Box sx={{ color: "text.secondary", display: "grid" }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                name={name}
                color="primary"
                inputProps={{ "aria-label": title }}
            />
        </Box>
    );
}

export default function FileForUpload(props) {
    const classes = useStyles();
    const { currentUserData } = useContext(UserContext);
    const { folders } = useContext(DatabaseContext);
    const fileCache = window.fileCache;

    const [displayName, setDisplayName] = useState("");

    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [events, setEvents] = useState([]);

    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);

    const [allowSharing, setAllowSharing] = useState(false);
    const [allowComments, setAllowComments] = useState(true);
    const [allowDownloads, setAllowDownloads] = useState(true);

    const [managers, setManagers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const [selectedGroups, setSelectedGroups] = useState([]);

    const [notificationsDialogOpen, setNotificationsDialogOpen] =
        useState(false);
    const [sendNotifications, setSendNotifications] = useState(false);
    const [sendEmails, setSendEmails] = useState(false);
    const [fromDate, setFromDate] = useState(dayjs());

    const [notificationMessage, setNotificationMessage] = useState("");

    const [showFileSizeText, setShowFileSizeText] = useState(false);
    const [fileSizeText, setFileSizeText] = useState("");

    // Get the actual file from the cache
    const actualFile = fileCache.get(props.fileMetadata.key);

    // Register this file's upload/settings functions with the parent.
    // Runs after every render (no dependency array) so the closures always
    // capture the latest state (display name, events, permissions, etc.),
    // and the cleanup unregisters on unmount. Previously this mutated
    // props.biRef during render, which breaks under StrictMode/concurrent
    // rendering.
    useEffect(() => {
        props.biRef.filesForUpload = props.biRef.filesForUpload.filter(
            (f) => f.key !== props.fileMetadata.key,
        );

        props.biRef.filesForUpload.push({
            key: props.fileMetadata.key,
            startUpload: startUpload,
            changeSettings: changeSettings,
        });

        return () => {
            props.biRef.filesForUpload = props.biRef.filesForUpload.filter(
                (f) => f.key !== props.fileMetadata.key,
            );
        };
    });

    useEffect(() => {
        setManagers(props.managers);

        if (
            props.managers.filter((m) => m.key === currentUserData.key)
                .length === 0
        ) {
            if (selectedUsers.length === 0) {
                setSelectedUsers([currentUserData]);
            }
        }

        if (actualFile && actualFile.size > 5000000000) {
            // 5,000,000,000 == 5GB
            setShowFileSizeText(true);

            let rounded = (actualFile.size / 1000000000).toFixed(2);
            setFileSizeText(rounded.toString() + "GB");

            props.showFileSizeMessage();
        }

        // eslint-disable-next-line
    }, [props.managers, actualFile]);

    function startUpload() {
        // Registers with the upload manager's queue. beginUpload is called
        // by the manager when a concurrency slot is free, and reused to
        // retry failed (non-resumable) uploads.
        uploadManager.start(props.fileMetadata, beginUpload);
    }

    function beginUpload() {
        if (!actualFile) {
            uploadManager.error(
                props.fileMetadata.key,
                "File is no longer available - please add it again",
            );
            return;
        }

        if (actualFile.type.includes("video")) {
            uploadVideoTUS();
        } else {
            if (actualFile.type === "application/pdf") {
                uploadPDFThumbnail();
            } else {
                uploadFile();
            }
        }
    }

    function uploadFile() {
        if (actualFile === null || props.fileMetadata.name === null) {
            console.log("File is undefined");
            uploadManager.error(props.fileMetadata.key, "File is undefined");
            return;
        }
        console.log("Starting File Upload");

        // Upload the file to firebase storage

        let storage = firebase.storage();

        let fileRef = storage
            .ref()
            .child(
                "files/" +
                    props.fileMetadata.key +
                    "/" +
                    props.fileMetadata.name,
            );

        let uploadTask = fileRef.put(actualFile);

        // Keep a reference so the upload can be cancelled from the uploads menu
        uploadManager.setTask(props.fileMetadata.key, {
            storageTask: uploadTask,
        });

        // Progress, error, and completion in a single observer
        uploadTask.on(
            firebase.storage.TaskEvent.STATE_CHANGED,
            function (snapshot) {
                const progress = Math.floor(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
                );

                uploadManager.progress(props.fileMetadata.key, progress);
            },
            function (error) {
                // User-initiated cancel - status already set by the manager
                if (error.code === "storage/canceled") return;

                console.error("Upload error:", error);
                uploadManager.error(props.fileMetadata.key, error.message);
            },
            function () {
                console.log("upload complete!");

                fileRef
                    .getDownloadURL()
                    .then((url) => {
                        addToFirebase(props.fileMetadata.key, url);
                    })
                    .catch((error) => {
                        console.error("Error getting download URL:", error);
                        uploadManager.error(
                            props.fileMetadata.key,
                            "Upload finished but the download URL could not be retrieved",
                        );
                    });
            },
        );
    }

    function uploadPDFThumbnail() {
        console.log("Uploading PDF Thumbnail");
        const importPDFCanvas = document.querySelector(
            ".import-pdf-page-" + props.fileMetadata.key + " canvas",
        );

        // If the hidden preview hasn't rendered yet (large PDF, quick upload
        // click), skip the thumbnail rather than crashing the whole upload
        if (!importPDFCanvas) {
            console.log(
                "PDF preview canvas not ready - uploading without thumbnail",
            );
            uploadFile();
            return;
        }

        let blob;

        try {
            blob = dataURItoBlob(importPDFCanvas.toDataURL("image/png"));
        } catch (error) {
            console.error("Could not create PDF thumbnail:", error);
            uploadFile();
            return;
        }

        let storage = firebase.storage();

        let fileRef = storage
            .ref()
            .child("files/" + props.fileMetadata.key + "/thumbnail");

        let thumbnailUploadTask = fileRef.put(blob);

        thumbnailUploadTask.on(
            firebase.storage.TaskEvent.STATE_CHANGED,
            null,
            function (error) {
                // The thumbnail is optional - upload the file anyway
                console.error("Thumbnail upload failed:", error);
                uploadFile();
            },
            function () {
                console.log("thumbnail upload complete!");

                fileRef
                    .getDownloadURL()
                    .then((url) => {
                        props.fileMetadata.thumbnail = url;
                        uploadFile();
                    })
                    .catch((error) => {
                        console.error("Error getting thumbnail URL:", error);
                        uploadFile();
                    });
            },
        );
    }

    function dataURItoBlob(dataURI) {
        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        var byteString = atob(dataURI.split(",")[1]);

        // separate out the mime component
        var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

        // write the bytes of the string to an ArrayBuffer
        var ab = new ArrayBuffer(byteString.length);

        // create a view into the buffer
        var ia = new Uint8Array(ab);

        // set the bytes of the buffer to the correct values
        for (var i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        // write the ArrayBuffer to a blob, and you're done
        var blob = new Blob([ab], { type: mimeString });
        return blob;
    }

    function uploadVideoTUS() {
        if (!actualFile || !props.fileMetadata.name) {
            console.log("File is undefined");
            uploadManager.error(props.fileMetadata.key, "File is undefined");
            return;
        }

        console.log("Starting Upload:", props.fileMetadata.name);

        const createBunnyVideo = firebase
            .functions()
            .httpsCallable("createBunnyVideo");

        createBunnyVideo({ title: props.fileMetadata.name })
            .then((result) => {
                const { guid, signature, expiration, libraryId } = result.data;

                const tusUpload = new tus.Upload(actualFile, {
                    endpoint: "https://video.bunnycdn.com/tusupload",
                    retryDelays: [
                        1, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000,
                        10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000,
                        18000, 19000, 20000, 25000, 30000, 40000, 50000, 60000,
                        70000, 80000, 90000, 100000,
                    ],
                    headers: {
                        AuthorizationSignature: signature,
                        AuthorizationExpire: expiration,
                        VideoId: guid,
                        LibraryId: libraryId,
                    },
                    metadata: {
                        filetype: props.fileMetadata.type,
                        title: props.fileMetadata.name,
                        filename: props.fileMetadata.name,
                    },
                    onError: function (error) {
                        console.error("Upload error:", error);
                        uploadManager.error(
                            props.fileMetadata.key,
                            error.message || "Video upload failed",
                        );
                    },
                    onProgress: function (bytesUploaded, bytesTotal) {
                        const progress = Math.floor(
                            (bytesUploaded / bytesTotal) * 100,
                        );

                        uploadManager.progress(
                            props.fileMetadata.key,
                            progress,
                        );
                    },
                    onSuccess: function () {
                        console.log("Upload success");
                        addToFirebase(guid);
                    },
                    onShouldRetry: function (err) {
                        console.log("Retry check:", err);
                        const status = err.originalResponse
                            ? err.originalResponse.getStatus()
                            : 0;

                        // Only retry transient failures: network drop (0),
                        // conflict (409), locked (423), and server errors.
                        // Everything else (403 expired signature, 400, 404)
                        // fails fast instead of retrying for 12 minutes.
                        return (
                            status === 0 ||
                            status === 409 ||
                            status === 423 ||
                            (status >= 500 && status < 600)
                        );
                    },
                });

                // Keep a reference so the upload can be aborted/resumed
                // from the uploads menu
                uploadManager.setTask(props.fileMetadata.key, {
                    tusUpload: tusUpload,
                });

                tusUpload.start();
            })
            .catch((error) => {
                console.error("Error creating Bunny video:", error);
                uploadManager.error(
                    props.fileMetadata.key,
                    "Could not create video: " + error.message,
                );
            });
    }

    function addToFirebase(key, url = "") {
        let eventsObject = {};
        for (let i = 0; i < events.length; i++) {
            let event = events[i];
            eventsObject[event.key] = true;
        }

        // Add to files
        var filesRef = firebase.database().ref("files");

        // If user didn't enter a display name, use the filename
        let displayNameForDatabase = displayName;

        if (displayNameForDatabase === "") {
            displayNameForDatabase = props.fileMetadata.name;
        }

        // Managers array to JSON
        let managersObject = {};
        if (managers) {
            managers.forEach((manager) => {
                managersObject[manager.key] = true;
            });
        }

        // Ancestors array to JSON
        let ancestorsObject = {};

        props.folder.ancestors.forEach((ancestor) => {
            ancestorsObject[ancestor] = true;
        });

        ancestorsObject[props.folder.key] = true;

        let thumbnail = "";
        if (props.fileMetadata.thumbnail) {
            thumbnail = props.fileMetadata.thumbnail;
        }

        var uploadedBy = "";
        if (currentUserData) {
            uploadedBy = currentUserData.key;
        }

        filesRef
            .child(key)
            .set({
                filename: props.fileMetadata.name,
                displayName: displayNameForDatabase,
                folder: props.folder.id,
                type: props.fileMetadata.type,
                allowDownloads: allowDownloads,
                allowComments: allowComments,
                allowSharing: allowSharing,
                managers: managersObject,
                events: eventsObject,
                groups: jsonFromArray(selectedGroups),
                ancestors: ancestorsObject,
                url: url,
                thumbnail: thumbnail,
                uploadedBy: uploadedBy,
            })
            .then(() => {
                // Only link events/users and send notifications once the
                // file record definitely exists

                // Add to any linked events

                var eventsRef = firebase.database().ref("events");

                for (let i = 0; i < events.length; i++) {
                    let event = events[i];

                    let fileObject = {};
                    fileObject[key] = true;
                    eventsRef
                        .child(event.key)
                        .child("files")
                        .update(fileObject);
                }

                let usersObjectForNotifications = {};

                // Fine users that have been added individually, add the file to users.files instead
                selectedUsers.forEach((user) => {
                    usersObjectForNotifications[user.key] = true;
                    if (!isUserInSelectedGroups(user)) {
                        var usersRef = firebase.database().ref("users");

                        let fileObject = {};
                        fileObject[key] = true;

                        usersRef
                            .child(user.key)
                            .child("files")
                            .update(fileObject);
                    }
                });

                // Add notifications

                if (sendNotifications) {
                    var notificationsRef = firebase
                        .database()
                        .ref("notifications");

                    var fileType = "file";

                    if (props.fileMetadata.type.includes("video")) {
                        fileType = "video";
                    } else if (props.fileMetadata.type === "application/pdf") {
                        fileType = "pdf";
                    } else if (props.fileMetadata.type.includes("audio")) {
                        fileType = "audio";
                    } else if (props.fileMetadata.type.includes("image")) {
                        fileType = "image";
                    }

                    let notificationObject = {
                        file: key,
                        title: displayNameForDatabase,
                        type: fileType,
                        users: usersObjectForNotifications,
                        managers: managersObject,
                        message: notificationMessage,
                        url: url,
                        thumbnail: thumbnail,
                        date: dayjs().toString(),
                        fromDate: fromDate.toString(),
                    };

                    let nearestHub = getAncestorHubs(
                        props.fileMetadata,
                        props.folder,
                        folders,
                        currentUserData,
                    ).reverse()[0];

                    if (nearestHub !== undefined) {
                        notificationObject.hubName = nearestHub.name;
                    }

                    notificationsRef.push(notificationObject);
                }

                if (sendEmails) {
                    sendFileEmails(
                        notificationMessage,
                        key,
                        displayNameForDatabase,
                    );
                }

                console.log("File Added Successfully");
                uploadManager.complete(props.fileMetadata.key);

                // Clean up the file from cache after successful upload
                fileCache.delete(props.fileMetadata.key);
            })
            .catch((error) => {
                // The bytes are uploaded but no database record exists -
                // without this, the user would think everything worked
                console.error("Error saving file record:", error);
                uploadManager.error(
                    props.fileMetadata.key,
                    "Upload finished but the file record could not be saved: " +
                        error.message,
                );
            });
    }

    function sendFileEmails(message, fileKey, fileDisplayName) {
        console.log("Sending File emails");

        const excludeManagers = false;

        var link = "https://nodiant.faw.cymru/";

        if (props.fileMetadata.type.includes("video")) {
            link += "video/";
        } else if (props.fileMetadata.type === "application/pdf") {
            link += "pdf/";
        } else if (props.fileMetadata.type.includes("audio")) {
            link += "audio/";
        } else if (props.fileMetadata.type.includes("image")) {
            link += "image/";
        } else {
            link += "file/";
        }

        link += fileKey;

        let allRecipients = [...selectedUsers];

        if (!excludeManagers) {
            allRecipients = [...allRecipients, ...managers];
        }

        // selectedUsers and managers overlap, and concatenating them used to
        // email anyone in both lists twice.
        const emailUsers = Array.from(
            new Map(
                allRecipients
                    .filter((user) => user && user.key)
                    .map((user) => [user.key, user]),
            ).values(),
        );

        if (emailUsers.length === 0) return;

        const messageSection = message
            ? `<tr>
                <td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #555555;">
                    ${message.replace(/\n/g, "<br>")}
                </td>
               </tr>`
            : "";

        const emailHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, Helvetica, sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
        <tr>
            <td align="center" style="padding: 20px 0;">
                <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <!-- Green Header Bar -->
                    <tr>
                        <td style="background-color: rgb(0, 149, 61); padding: 20px 30px;">
                            <h1 style="margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 22px; color: #ffffff;">Nodiant Online</h1>
                        </td>
                    </tr>
                    <!-- Content -->
                    <tr>
                        <td style="padding: 30px 30px 10px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #333333;">
                            A new file has been shared with you:
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 18px; font-weight: bold; color: #333333;">
                            ${fileDisplayName}
                        </td>
                    </tr>
                    ${messageSection}
                    <!-- Button -->
                    <tr>
                        <td style="padding: 0 30px 30px 30px;">
                            <a href="${link}" style="display: inline-block; padding: 12px 24px; background-color: rgb(0, 149, 61); color: #ffffff; text-decoration: none; border-radius: 4px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold;">View File</a>
                        </td>
                    </tr>
                    <!-- White Footer Bar with Logo -->
                    <tr>
                        <td style="background-color: #ffffff; border-top: 1px solid #e0e0e0; padding: 15px 30px; text-align: center;">
                            <img src="https://firebasestorage.googleapis.com/v0/b/nodiant-e20a8.appspot.com/o/FAW_CLEAR.png?alt=media&token=a5b922df-5982-4fd9-af85-5609bb390d55" alt="FAW Logo" height="40" style="height: 40px;">
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;

        // Not awaited by the upload, and a failed notification must never
        // fail the upload — so swallow the error here as before.
        sendSharedItemEmails({
            embargoNotifications: true,
            firebase,
            html: emailHtml,
            label: `file: ${fileDisplayName}`,
            notificationDate: fromDate,
            recipients: emailUsers,
            subject: "Nodiant Online - New File Available",
        }).catch((error) => {
            console.log("Could not send file notification emails");
            console.log("Code:", error.code);
            console.log("Message:", error.message);
        });
    }

    function isUserInSelectedGroups(user) {
        for (let i = 0; i < selectedGroups.length; i++) {
            let group = selectedGroups[i];
            for (let j = 0; j < group.users.length; j++) {
                let u = group.users[j];

                if (user.key === u.key) {
                    return true;
                }
            }
        }
        return false;
    }

    function handleChangeDisplayName(event) {
        setDisplayName(event.target.value);
    }

    function handleEventClick() {
        setEventDialogOpen(true);
    }

    function handleCopyClick() {
        const settings = {
            events: events,
            allowComments: allowComments,
            allowSharing: allowSharing,
            allowDownloads: allowDownloads,
            selectedUsers: selectedUsers,
            selectedGroups: selectedGroups,
            managers: managers,
            sendNotifications: sendNotifications,
            sendEmails: sendEmails,
            notificationMessage: notificationMessage,
            fromDate: fromDate,
        };

        props.handleCopyClick(settings);
    }

    // Allows us to change the settings from elsewhere, this is used when copying settings of one file to another
    function changeSettings(settings) {
        settings.events.forEach((event) => {
            event.files.push(props.fileMetadata.key);
        });

        setSendNotifications(settings.sendNotifications);
        setSendEmails(settings.sendEmails);
        setNotificationMessage(settings.notificationMessage);
        setFromDate(settings.fromDate);
        setEvents(settings.events);
        setAllowComments(settings.allowComments);
        setAllowDownloads(settings.allowDownloads);
        setAllowSharing(settings.allowSharing);
        setSelectedUsers(settings.selectedUsers);
        setSelectedGroups(settings.selectedGroups);
        setManagers(settings.managers);
    }

    function handlePermissionsClick() {
        setPermissionsDialogOpen(true);
    }

    function handleNotificationsClick() {
        setNotificationsDialogOpen(true);
    }

    function handleChange(event) {
        if (event.target.name === "downloads") {
            setAllowDownloads(event.target.checked);
        } else if (event.target.name === "comments") {
            setAllowComments(event.target.checked);
        } else if (event.target.name === "sharing") {
            setAllowSharing(event.target.checked);
        }
    }

    function handleEventSelectionChange(selectedEvents) {
        setEvents(selectedEvents);
    }

    function handleCloseEventDialog(selectedEvents) {
        setEvents(selectedEvents);
        setEventDialogOpen(false);
    }

    function handleClosePermissionsDialog(users, groups) {
        setSelectedGroups(groups);
        setSelectedUsers(users);
        setPermissionsDialogOpen(false);
    }

    function handleCloseNotificationsDialog(settings) {
        setSendNotifications(settings.sendNotifications);
        setSendEmails(settings.sendEmails);
        setNotificationMessage(settings.message);
        setFromDate(settings.fromDate);

        setNotificationsDialogOpen(false);
    }

    function handlePressRemoveButton() {
        // Remove file from cache when removing from UI
        fileCache.delete(props.fileMetadata.key);
        props.handleRemoveClick(props.fileMetadata);
    }

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    position: "relative",
                    borderRadius: 3,
                    borderColor: "divider",
                    overflow: "hidden",
                    boxShadow: "0 4px 18px rgba(0, 0, 0, 0.12)",
                }}
            >
                <Document
                    file={actualFile}
                    className={classes.pdfPreview}
                    loading={<></>}
                    error={<></>}
                    noData={<></>}
                    aria-hidden="true"
                >
                    <Page
                        pageNumber={1}
                        className={"import-pdf-page-" + props.fileMetadata.key}
                        height={112}
                        width={200}
                    />
                </Document>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 1.5, sm: 2.25 },
                        py: 1.75,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <UploadFileTypeIcon type={props.fileMetadata.type} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 800,
                                lineHeight: 1.3,
                                overflowWrap: "anywhere",
                            }}
                        >
                            {props.fileMetadata.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {props.fileMetadata.type || "File"}
                        </Typography>
                    </Box>
                    {showFileSizeText && (
                        <Chip
                            icon={<WarningAmberOutlinedIcon />}
                            label={fileSizeText}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ display: { xs: "none", sm: "flex" } }}
                        />
                    )}
                    <Tooltip title="Remove file">
                        <IconButton
                            aria-label={`Remove ${props.fileMetadata.name}`}
                            onClick={handlePressRemoveButton}
                            size="small"
                            sx={{ mt: -0.25, mr: -0.5 }}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "minmax(0, 1.25fr) minmax(300px, 0.75fr)",
                        },
                        gap: { xs: 2, md: 2.5 },
                        p: { xs: 1.5, sm: 2.25 },
                        backgroundColor: "background.subtle",
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                        >
                            File details
                        </Typography>
                        <TextField
                            value={displayName}
                            onChange={handleChangeDisplayName}
                            label="Display name"
                            placeholder={props.fileMetadata.name}
                            helperText="Leave blank to use the original filename."
                            fullWidth
                            variant="outlined"
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "background.paper",
                                },
                            }}
                        />

                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                        >
                            File settings
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                },
                                gap: 1,
                            }}
                        >
                            <ButtonBase
                                onClick={handleEventClick}
                                sx={settingButtonSx}
                            >
                                <Badge
                                    badgeContent={events.length}
                                    color="primary"
                                    max={99}
                                >
                                    <EventNoteIcon color="primary" />
                                </Badge>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Events
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {events.length === 0
                                            ? "No linked events"
                                            : `${events.length} linked`}
                                    </Typography>
                                </Box>
                                <ChevronRightIcon
                                    sx={{ color: "text.disabled" }}
                                />
                            </ButtonBase>

                            <ButtonBase
                                onClick={handleNotificationsClick}
                                sx={settingButtonSx}
                            >
                                <NotificationsIcon color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Notifications
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {sendNotifications || sendEmails
                                            ? "Configured"
                                            : "Not configured"}
                                    </Typography>
                                </Box>
                                <ChevronRightIcon
                                    sx={{ color: "text.disabled" }}
                                />
                            </ButtonBase>

                            <ButtonBase
                                onClick={handlePermissionsClick}
                                sx={settingButtonSx}
                            >
                                <GroupIcon color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Access
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {selectedUsers.length + managers.length}{" "}
                                        people selected
                                    </Typography>
                                </Box>
                                <ChevronRightIcon
                                    sx={{ color: "text.disabled" }}
                                />
                            </ButtonBase>

                            <ButtonBase
                                onClick={handleCopyClick}
                                sx={settingButtonSx}
                            >
                                <FileCopyIcon color="primary" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Copy settings
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Apply to every file
                                    </Typography>
                                </Box>
                            </ButtonBase>
                        </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                        >
                            Permissions
                        </Typography>
                        <Box
                            sx={{
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: "divider",
                                overflow: "hidden",
                                backgroundColor: "background.paper",
                            }}
                        >
                            <AvailabilityRow
                                icon={<DownloadOutlinedIcon />}
                                title="Downloads"
                                description="Allow users to download this file."
                                checked={allowDownloads}
                                onChange={handleChange}
                                name="downloads"
                            />
                            <AvailabilityRow
                                icon={<ChatBubbleOutlineIcon />}
                                title="Comments"
                                description="Allow users to discuss this file."
                                checked={allowComments}
                                onChange={handleChange}
                                name="comments"
                                divided
                            />
                            <AvailabilityRow
                                icon={<ShareOutlinedIcon />}
                                title="Sharing"
                                description="Allow a public download link."
                                checked={allowSharing}
                                onChange={handleChange}
                                name="sharing"
                                divided
                            />
                        </Box>
                    </Box>
                </Box>
            </Paper>
            <LinkFileToEventDialog
                open={eventDialogOpen}
                onClose={handleCloseEventDialog}
                selectionChanged={handleEventSelectionChange}
                selectedEvents={events}
                calendars={props.calendars}
            />
            <FilePermissionsDialog
                open={permissionsDialogOpen}
                onClose={handleClosePermissionsDialog}
                selectedUsers={selectedUsers}
                selectedGroups={selectedGroups}
                managers={managers}
                ancestorHubs={props.ancestorHubs}
            />
            <FileNotificationsDialog
                open={notificationsDialogOpen}
                onClose={handleCloseNotificationsDialog}
                message={notificationMessage}
                send={sendNotifications}
                email={sendEmails}
                fromDate={fromDate}
                recipientCount={
                    new Set(
                        [...selectedUsers, ...managers]
                            .map((user) => user.key)
                            .filter(Boolean),
                    ).size
                }
            />
        </>
    );
}
